import { Schema } from "effect";
import { EgkAttestationHandle } from "./egk-attestation.schema";
import { EligibleContactType } from "./contact-assertion.schema";
import { PatientInfoPolicyFields } from "./patient-info-policy.schema";

/**
 * Opaque client-facing identifier for one reviewable contact.
 *
 * A server-minted random alias, never a Doc Cirrus communication ID. The mapping from alias to
 * vendor ID lives only in the server's capability record, so a client cannot address a contact the
 * review did not offer it, and vendor identifiers never reach the kiosk.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { Schema } from "effect";
 * import { ReviewItemId } from "@ascleo/frontdesk-contracts/patient-info/review";
 *
 * const id = Schema.decodeUnknownSync(ReviewItemId)("itm_7f3a9c1e");
 * ```
 */
export const ReviewItemId = Schema.String.pipe(
  Schema.check(Schema.isMinLength(1)),
  Schema.check(Schema.isMaxLength(128)),
  Schema.brand("ReviewItemId"),
);

/**
 * The decoded type of a {@link ReviewItemId}.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { ReviewItemId } from "@ascleo/frontdesk-contracts/patient-info/review";
 *
 * function key(id: ReviewItemId): string {
 *   return id;
 * }
 * ```
 */
export type ReviewItemId = typeof ReviewItemId.Type;

/**
 * Why a contact is read-only for direct patient writes.
 *
 * `confirmed`, `confirmation-pending`, and `portal-linked` are proven vendor states. `unverifiable`
 * is the conservative catch-all: when protection state cannot be proven from the live contract, the
 * contact is protected rather than assumed ordinary. Each value maps to patient-facing copy
 * explaining that reception will review the change.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { Schema } from "effect";
 * import { ContactProtectionReason } from "@ascleo/frontdesk-contracts/patient-info/review";
 *
 * Schema.decodeUnknownSync(ContactProtectionReason)("portal-linked");
 * ```
 */
export const ContactProtectionReason = Schema.Literals(
  ["confirmed",
  "confirmation-pending",
  "portal-linked",
  "unverifiable"],
);

/**
 * The decoded type of a {@link ContactProtectionReason}.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { ContactProtectionReason } from "@ascleo/frontdesk-contracts/patient-info/review";
 *
 * const reason: ContactProtectionReason = "confirmed";
 * ```
 */
export type ContactProtectionReason = typeof ContactProtectionReason.Type;

/**
 * One contact offered to the patient for review.
 *
 * `value` is the raw current value: the patient must see what is on file in order to correct it, and
 * masking is a client-side presentation concern, not the authorization boundary — the capability is.
 * The value therefore lives only in this gated response and in volatile client memory. It is never
 * written to Mongo, logs, telemetry, or device storage.
 *
 * `protection` present means the contact is read-only for direct writes: a proposed change becomes a
 * reception task rather than a Doc Cirrus write. Absent means ordinary — direct add/edit/delete is
 * permitted.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { Schema } from "effect";
 * import { ReviewContact } from "@ascleo/frontdesk-contracts/patient-info/review";
 *
 * const contact = Schema.decodeUnknownSync(ReviewContact)({
 *   itemId: "itm_7f3a9c1e",
 *   type: "MOBILEPRIV",
 *   value: "+49 30 1234567",
 *   isPrimary: true,
 * });
 * ```
 */
export const ReviewContact = Schema.Struct({
  itemId: ReviewItemId,
  type: EligibleContactType,
  value: Schema.String,
  isPrimary: Schema.Boolean,
  protection: Schema.optional(ContactProtectionReason),
});

/**
 * The decoded type of a {@link ReviewContact}.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { ReviewContact } from "@ascleo/frontdesk-contracts/patient-info/review";
 *
 * function isOrdinary(contact: ReviewContact): boolean {
 *   return contact.protection === undefined;
 * }
 * ```
 */
export type ReviewContact = typeof ReviewContact.Type;

/**
 * Request to open (or re-read) the patient-info review.
 *
 * Carries only the opaque attestation handle. There is deliberately no `patientId`: the server reads
 * the patient its own record is bound to, so knowing a patient identifier can never select the read
 * target.
 *
 * Activation is idempotent for the same committed flow — a repeat call while the capability is active
 * returns a fresh read rather than minting a second authority.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { Schema } from "effect";
 * import { PatientInfoReviewRequest } from "@ascleo/frontdesk-contracts/patient-info/review";
 *
 * const request = Schema.decodeUnknownSync(PatientInfoReviewRequest)({
 *   egkAttestation: "f3a9c1e0b7d2",
 * });
 * ```
 */
export const PatientInfoReviewRequest = Schema.Struct({
  egkAttestation: EgkAttestationHandle,
});

/**
 * The decoded type of a {@link PatientInfoReviewRequest}.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { PatientInfoReviewRequest } from "@ascleo/frontdesk-contracts/patient-info/review";
 *
 * function handleOf(request: PatientInfoReviewRequest): string {
 *   return request.egkAttestation;
 * }
 * ```
 */
export type PatientInfoReviewRequest = typeof PatientInfoReviewRequest.Type;

/**
 * The capability-gated patient review.
 *
 * Contains the eligible phone/email contacts and the current free-text Hausarzt. Hidden communication
 * types are absent entirely — not even their count is disclosed — while remaining present in the
 * typed vendor value the server preserves on every write. `hausarzt` is `null` when none is stored.
 *
 * `expiresAt` is the hard capability deadline. It never slides on re-read, so the client can show
 * remaining time and stop retrying honestly once it passes.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { Schema } from "effect";
 * import { PatientInfoReview } from "@ascleo/frontdesk-contracts/patient-info/review";
 *
 * const review = Schema.decodeUnknownSync(PatientInfoReview)({
 *   fields: { phone: true, email: true, hausarzt: true },
 *   contacts: [],
 *   hausarzt: null,
 *   expiresAt: "2026-07-17T09:41:00.000Z",
 * });
 * ```
 */
export const PatientInfoReview = Schema.Struct({
  fields: PatientInfoPolicyFields,
  contacts: Schema.Array(ReviewContact),
  hausarzt: Schema.NullOr(Schema.String),
  expiresAt: Schema.DateTimeUtcFromString,
});

/**
 * The decoded type of a {@link PatientInfoReview}.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { PatientInfoReview } from "@ascleo/frontdesk-contracts/patient-info/review";
 *
 * function contactCount(review: PatientInfoReview): number {
 *   return review.contacts.length;
 * }
 * ```
 */
export type PatientInfoReview = typeof PatientInfoReview.Type;

/**
 * Request to revoke the capability and destroy server-side review state.
 *
 * Sent best-effort on skip, reset, timeout, patient switch, and unmount. The client clears its own
 * state regardless of whether this call succeeds — an offline kiosk must never be blocked from
 * resetting — and the hard expiry plus TTL remain the backstop.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { Schema } from "effect";
 * import { PatientInfoRevokeRequest } from "@ascleo/frontdesk-contracts/patient-info/review";
 *
 * const request = Schema.decodeUnknownSync(PatientInfoRevokeRequest)({
 *   egkAttestation: "f3a9c1e0b7d2",
 * });
 * ```
 */
export const PatientInfoRevokeRequest = Schema.Struct({
  egkAttestation: EgkAttestationHandle,
});

/**
 * The decoded type of a {@link PatientInfoRevokeRequest}.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { PatientInfoRevokeRequest } from "@ascleo/frontdesk-contracts/patient-info/review";
 *
 * function handleOf(request: PatientInfoRevokeRequest): string {
 *   return request.egkAttestation;
 * }
 * ```
 */
export type PatientInfoRevokeRequest = typeof PatientInfoRevokeRequest.Type;

/**
 * Result of a revoke call.
 *
 * Idempotent and deliberately uninformative: repeated revokes succeed identically and never disclose
 * whether a record existed or what state it was in.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { Schema } from "effect";
 * import { PatientInfoRevokeResult } from "@ascleo/frontdesk-contracts/patient-info/review";
 *
 * Schema.decodeUnknownSync(PatientInfoRevokeResult)({ revoked: true });
 * ```
 */
export const PatientInfoRevokeResult = Schema.Struct({
  revoked: Schema.Literal(true),
});

/**
 * The decoded type of a {@link PatientInfoRevokeResult}.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { PatientInfoRevokeResult } from "@ascleo/frontdesk-contracts/patient-info/review";
 *
 * const result: PatientInfoRevokeResult = { revoked: true };
 * ```
 */
export type PatientInfoRevokeResult = typeof PatientInfoRevokeResult.Type;
